import type { EpssStatus } from '@/lib/types'

const EPSS_TITLES = {
  pending: 'CVE was ingested after the latest EPSS refresh.',
  not_scored: 'FIRST EPSS has no score for this CVE after the latest refresh.',
  unknown: 'EPSS status cannot be classified from current health data.',
} as const

export interface EpssDisplay {
  label: string
  title: string
  isScore: boolean
}

function isEpssStatus(value: unknown): value is EpssStatus {
  return (
    value === 'scored' ||
    value === 'pending' ||
    value === 'not_scored' ||
    value === 'unknown'
  )
}

export function normaliseEpssProbability(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= 0 && value <= 1 ? value : null
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed.length === 0) return null
    if (!/^(?:\d+(?:\.\d+)?|\.\d+)$/.test(trimmed)) return null

    const parsed = Number(trimmed)
    return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : null
  }

  return null
}

function unknownDisplay(): EpssDisplay {
  return {
    label: 'Unknown',
    title: EPSS_TITLES.unknown,
    isScore: false,
  }
}

export function formatEpssDisplay(vulnerability: {
  epssScore?: unknown
  epssStatus?: unknown
}): EpssDisplay {
  const { epssScore, epssStatus } = vulnerability

  if (!isEpssStatus(epssStatus)) return unknownDisplay()

  switch (epssStatus) {
    case 'scored': {
      const probability = normaliseEpssProbability(epssScore)
      if (probability === null) return unknownDisplay()

      return {
        label: `${(probability * 100).toFixed(1)}%`,
        title: '',
        isScore: true,
      }
    }
    case 'pending':
      return {
        label: 'Pending',
        title: EPSS_TITLES.pending,
        isScore: false,
      }
    case 'not_scored':
      return {
        label: 'Not scored',
        title: EPSS_TITLES.not_scored,
        isScore: false,
      }
    case 'unknown':
      return unknownDisplay()
  }

  return unknownDisplay()
}
